import { ContentType, type ClassificationContent } from '@classification-testing/domain';
import { convertAudioToWav } from '@classification-testing/utils';
import { track } from '@core/logging';
import { useState, useCallback, useRef } from 'react';

interface UseAudioRecordingProps {
  onContentChange: (content: ClassificationContent) => void;
  onFileReady?: (file: File) => void;
}

interface UseAudioRecordingResult {
  isRecording: boolean;
  startRecording: () => void;
  stopRecording: () => void;
}

/**
 * Simplified audio recording hook
 */
export function useAudioRecording({ onContentChange, onFileReady }: UseAudioRecordingProps): UseAudioRecordingResult {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      track('RecordAudioInitiated');

      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Use best available format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          const recordedBlob = new Blob(chunksRef.current, { type: mimeType });
          const wavBlob = await convertAudioToWav(recordedBlob);
          const audioUrl = URL.createObjectURL(wavBlob);
          const audioFile = new File([wavBlob], `recording-${Date.now()}.wav`, { type: 'audio/wav' });

          onContentChange({
            type: ContentType.AUDIO,
            audioUrl,
            audioName: audioFile.name,
          });

          onFileReady?.(audioFile);

          track('RecordAudioSucceeded', {
            fileSize: String(audioFile.size),
          });

          // Cleanup
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
          mediaRecorderRef.current = null;
        } catch (error) {
          track('RecordAudioFailed', {
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
          });

          setIsRecording(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      setIsRecording(false);
    }
  }, [onContentChange, onFileReady]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      track('RecordAudioStopped');

      mediaRecorderRef.current.stop();
    }
  }, [isRecording]);

  return {
    isRecording,
    startRecording,
    stopRecording,
  };
}
